import React, { useEffect, useState } from 'react';
import './styles.scss';
import useRestaurant from 'CustomHooks/useRestaurant';
import Modal from 'Components/Molecules/Modal';
import SwitchBox from 'Components/Atoms/Switch';
import TextField from 'Components/Atoms/TextField';
import { IconButton, Tab, Tabs } from '@material-ui/core';
import GlobalFlagsTab from './GlobalFlagsTab';
import { ArrowDownward, ArrowUpward, Delete } from '@material-ui/icons';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import { FieldValue } from 'utils/firebase';

function changePositionInArray<T = any>(
  arr: T[],
  index: number,
  newIndex: number,
) {
  return arr.map((x, i, arr) => {
    if (i === index) return arr[newIndex];
    if (i === newIndex) return arr[index];
    return x;
  });
}

export type ExperimentalFlagsProps = {};
export type GlobalFlags = {
  deactivateGuestComment?: boolean;
  deactivateReminderEmail?: boolean;
  deactivateFeedbackEmail?: boolean;
  phoneDefaultCountry?: string;
};
export type ExperimentalFlags = {
  allowAllTableCombinations?: boolean | undefined;
  hideTableSizeWarning?: boolean | undefined;
  hideShiftFilterWarning?: boolean | undefined;
  allowMultipleSpaces?: boolean | undefined;
  dragSelectTables?: boolean | undefined;
  switchSpaceOnDrag?: boolean | undefined;
  multiTableGroupColors?: boolean | undefined;
  emails?: string;
  disableSecurityForAdmins?: boolean;
  showAllTimesInEditMode?: boolean;
  blockPerShift?: boolean;

  // gilden im zims
  disableRelocateMessage?: boolean;
  hideWalkIns?: boolean;
  truncateTableNames?: boolean;
  truncateComments?: boolean;
  customFieldBeingLate?: boolean;
  hideToasts?: boolean;

  // Meissl & Schadn
  showGuestsInTimeEvenOnLargeFloorplans?: boolean;
  disableShiftCommentAtBottom?: boolean;
  phoneDefaultCountry?: string; // default is de
  editGuestComment?: boolean;
  hideTimeOfCurrentReservation?: boolean;
  quandooTableView?: boolean;
  allowMultipleStati?: boolean;
  currentTableStati?: {
    title: string;
    color: string;
    chairColor: string;
    textColor: string;
    icon: string;
  }[];
  disabledCustomFields?: string;
  sidebarWidth?: string;
  sidebarFontSize?: string;

  hidePlusFifteen?: boolean;
  showCode?: boolean;
  disableDragAndDrop?: boolean;
  paidButton?: boolean;
  onlyShowReservationsThatStartInCurrentShift?: boolean;
  walkinDefaultOccassion?: string;
  hidePlacedGuests?: boolean;
  autoSetTableToDoneAfterEnd?: boolean;
  confirmationEmailOffByDefault?: boolean;

  // walkin
  useMaxGuestsForWalkin?: boolean;

  // projektilxfrauenbad
  additionalInternalGastronautFee?: { value: number; type: 'percentage' | 'fixed' };
};

// ─── VS-Code-style settings primitives ─────────────────────────────────────
//
// `Section`     — bold heading + bottom border, groups related flags.
// `FlagToggle`  — title + description + on/off switch.
// `FlagInput`   — title + description + free-text/number input.
//
// All three render German labels literally (translation={null}) so they are
// not looked up in the i18n catalog.

export const Section: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="exp-flags__section">
    <Typography
      variant="h5"
      translation={null}
      block
      className="exp-flags__section-title"
    >
      {title}
    </Typography>
    {children}
  </div>
);

export const FlagToggle: React.FC<{
  title: string;
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}> = ({ title, description, checked, onChange }) => (
  <SwitchBox
    className="exp-flags__toggle"
    checked={checked}
    onChange={(_, next) => onChange(next)}
    label={
      <span className="exp-flags__label">
        <Typography variant="text-2" weight="medium" translation={null} block>
          {title}
        </Typography>
        {description && (
          <Typography
            variant="text-3"
            color="subdued"
            translation={null}
            block
            className="exp-flags__description"
          >
            {description}
          </Typography>
        )}
      </span>
    }
  />
);

export const FlagInput: React.FC<{
  title: string;
  description?: string;
  value: string;
  type?: string;
  onChange: (next: string) => void;
}> = ({ title, description, value, type, onChange }) => (
  <div className="exp-flags__input-row">
    <Typography variant="text-2" weight="medium" translation={null} block>
      {title}
    </Typography>
    {description && (
      <Typography
        variant="text-3"
        color="subdued"
        translation={null}
        block
        className="exp-flags__description"
      >
        {description}
      </Typography>
    )}
    <TextField
      fullWidth
      value={value}
      type={type}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// ─── Component ─────────────────────────────────────────────────────────────

const ExperimentalFlags: React.FC<ExperimentalFlagsProps> = ({}) => {
  const { experimentalFlags } = useRestaurant();

  const [open, setopen] = useState(false);
  const [tab, setTab] = useState<'restaurant' | 'global'>('restaurant');

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'e' && e.ctrlKey) {
        e.preventDefault();
        setopen(true);
      }
    }

    if (window) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (window) {
        window.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, []);

  const ref = experimentalFlags.ref;
  const data = experimentalFlags?.data;

  const setFlag = (patch: Partial<ExperimentalFlags>) => {
    ref.set(patch, { merge: true });
  };

  return (
    <Modal
      open={open}
      onClose={() => setopen(false)}
      title="Erweiterte Einstellungen"
      maxWidth="sm"
      fullWidth
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        className="exp-flags__tabs"
      >
        <Tab label="Dashboard" value="restaurant" />
        <Tab label="Global" value="global" />
      </Tabs>

      {tab === 'global' && <GlobalFlagsTab />}

      {tab === 'restaurant' && (
        <>
          <Typography
            variant="text-3"
            color="subdued"
            translation={null}
            block
            className="exp-flags__intro"
          >
            Restaurant-spezifische Schalter für Verhalten und Darstellung. Nicht
            alle Optionen sind für jeden Betrieb sinnvoll – bitte nur ändern,
            wenn der Effekt klar ist.
          </Typography>

          {/* ── Tickets ─────────────────────────────────────────────────────── */}
          <Section title="Tickets">
            <FlagInput
              title="Interne Gastronaut-Provision (%)"
              description="Prozentualer Anteil des Ticketpreises, der als interne Provision ausgewiesen und von der angezeigten Summe abgezogen wird."
              type="number"
              value={
                data?.additionalInternalGastronautFee?.value?.toString() ?? ''
              }
              onChange={(value) =>
                setFlag({
                  additionalInternalGastronautFee: {
                    value: Number(value.replace(',', '.')) || 0,
                    type: 'percentage',
                  },
                })
              }
            />
          </Section>

          {/* ── Tischplan ───────────────────────────────────────────────────── */}
          <Section title="Tischplan">
            <FlagToggle
              title="Quandoo-Tischansicht"
              description="Zeigt unter dem Tisch ein Etikett mit Gästezahl und Name der nächsten Reservierung, die Uhrzeit am Tisch steht dann ohne Gästezahl. Beim Anlegen oder Bearbeiten einer Reservierung fällt das Etikett weg und die Gästezahl steht wieder neben der Uhrzeit."
              checked={!!data?.quandooTableView}
              onChange={(quandooTableView) => setFlag({ quandooTableView })}
            />
            <FlagToggle
              title="Gästezahl auch bei großen Tischplänen anzeigen"
              description="Die Anzahl der Gäste erscheint immer neben der Uhrzeit am Tisch — unabhängig von der Größe des Tischplans."
              checked={!!data?.showGuestsInTimeEvenOnLargeFloorplans}
              onChange={(showGuestsInTimeEvenOnLargeFloorplans) =>
                setFlag({ showGuestsInTimeEvenOnLargeFloorplans })
              }
            />
            <FlagToggle
              title="Uhrzeit der aktiven Reservierung ausblenden"
              description="Reservierungen, deren Gäste bereits sitzen, werden ohne Uhrzeit dargestellt – der Tischplan wirkt aufgeräumter."
              checked={!!data?.hideTimeOfCurrentReservation}
              onChange={(hideTimeOfCurrentReservation) =>
                setFlag({ hideTimeOfCurrentReservation })
              }
            />
            <FlagToggle
              title="Lange Tischnamen kürzen"
              description="Tischnamen, die zu lang für die Reservierungsanzeige sind, werden mit drei Punkten abgekürzt."
              checked={!!data?.truncateTableNames}
              onChange={(truncateTableNames) => setFlag({ truncateTableNames })}
            />
            <FlagToggle
              title="Im Bearbeitungsmodus alle Reservierungen am Tisch zeigen"
              description="Beim Anlegen oder Verschieben einer Reservierung bleiben alle anderen Reservierungen am Tisch sichtbar – hilft beim Erkennen von Konflikten."
              checked={!!data?.showAllTimesInEditMode}
              onChange={(showAllTimesInEditMode) =>
                setFlag({ showAllTimesInEditMode })
              }
            />
            <FlagToggle
              title="Tische und Bereiche pro Schicht sperren"
              description="Tische und Bereiche lassen sich für einzelne Schichten statt nur für den ganzen Tag sperren. In der Tisch-Seitenleiste steht dafür unter „Tisch blockieren“ der Knopf „Tisch für Schicht blockieren“, das Schloss neben der Bereichsauswahl öffnet ein Fenster mit den Schichten. Gesperrte Zeiten stehen als Flächen in der Zeitachse. Ohne den Schalter sperrt ein Klick wie bisher den ganzen Tag."
              checked={!!data?.blockPerShift}
              onChange={(blockPerShift) => setFlag({ blockPerShift })}
            />
            <FlagToggle
              title="Drag & Drop deaktivieren"
              description="Reservierungen lassen sich nicht mehr per Drag & Drop verschieben. Stattdessen öffnet ein langer Druck das Kommentar-Modal – empfohlen für reine Touch-Geräte."
              checked={!!data?.disableDragAndDrop}
              onChange={(disableDragAndDrop) => setFlag({ disableDragAndDrop })}
            />
            <FlagToggle
              title="Beliebige Tischkombinationen erlauben"
              description="Hebt die Beschränkungen aus den Tischplan-Einstellungen auf. Tische können frei kombiniert werden, auch wenn sie sonst nicht zusammenpassen."
              checked={!!data?.allowAllTableCombinations}
              onChange={(allowAllTableCombinations) =>
                setFlag({ allowAllTableCombinations })
              }
            />
            <FlagToggle
              title="Mehrere Bereiche kombinieren"
              description="Erlaubt es, Tische aus verschiedenen Bereichen (z. B. Innen + Außen) für eine Reservierung zu nutzen."
              checked={!!data?.allowMultipleSpaces}
              onChange={(allowMultipleSpaces) =>
                setFlag({ allowMultipleSpaces })
              }
            />
            <FlagToggle
              title="Tische per Wisch-Geste auswählen"
              description="Im Bearbeitungsmodus lassen sich mehrere Tische auf einmal an- oder abwählen, indem man den Finger oder die Maus über sie zieht. Die Aktion (auswählen oder abwählen) richtet sich nach dem ersten berührten Tisch."
              checked={!!data?.dragSelectTables}
              onChange={(dragSelectTables) => setFlag({ dragSelectTables })}
            />
            <FlagToggle
              title="Bereichswechsel beim Reservierung-Ziehen"
              description="Während eine Reservierung gezogen wird, verwandelt sich der Bereichs-Wahlschalter unten links in eine Reihe von Buttons. Wird ein Button überfahren, wechselt der Tischplan auf den jeweiligen Bereich, sodass die Reservierung auf einem Tisch in einem anderen Bereich abgelegt werden kann."
              checked={!!data?.switchSpaceOnDrag}
              onChange={(switchSpaceOnDrag) => setFlag({ switchSpaceOnDrag })}
            />
            <FlagToggle
              title="Warnung zur Tischgröße ausblenden"
              description="Es wird kein Hinweis mehr angezeigt, wenn die Anzahl der Gäste nicht zur Kapazität des gewählten Tisches passt."
              checked={!!data?.hideTableSizeWarning}
              onChange={(hideTableSizeWarning) =>
                setFlag({ hideTableSizeWarning })
              }
            />
            <FlagToggle
              title="Warnung beim Schichtfilter ausblenden"
              description="Ist eine spätere Schicht ausgewählt, erscheint kein roter Hinweis mehr, dass die laufenden Reservierungen nicht im Blick sind. Sie bleiben in dieser Ansicht ausgeblendet, bis die aktuelle Schicht oder „Ganzer Tag“ gewählt wird."
              checked={!!data?.hideShiftFilterWarning}
              onChange={(hideShiftFilterWarning) =>
                setFlag({ hideShiftFilterWarning })
              }
            />
            <FlagToggle
              title="Farbige Ringe für Tischgruppen"
              description="Reservierungen über mehrere Tische markieren jeden ihrer Tische dauerhaft mit einem farbigen Ring in einer festen Gruppenfarbe – so ist auf einen Blick erkennbar, welche Tische zusammengehören, ohne die Reservierung anzuklicken."
              checked={!!data?.multiTableGroupColors}
              onChange={(multiTableGroupColors) =>
                setFlag({ multiTableGroupColors })
              }
            />
            <FlagToggle
              title="Schicht-Kommentar unten ausblenden"
              description="Der Kommentar zur aktuellen Schicht erscheint nicht mehr am unteren Rand des Tischplans."
              checked={!!data?.disableShiftCommentAtBottom}
              onChange={(disableShiftCommentAtBottom) =>
                setFlag({ disableShiftCommentAtBottom })
              }
            />
          </Section>

          {/* ── Reservierungen ─────────────────────────────────────────────── */}
          <Section title="Reservierungen">
            <FlagToggle
              title="Walk-Ins ausblenden"
              description="Walk-In-Reservierungen werden in der Reservierungsliste und der Sidebar nicht angezeigt."
              checked={!!data?.hideWalkIns}
              onChange={(hideWalkIns) => setFlag({ hideWalkIns })}
            />
            <FlagToggle
              title="Lange Kommentare kürzen"
              description="Kommentare in der Reservierungsliste werden auf eine Zeile gekürzt; der vollständige Text erscheint im Tooltip."
              checked={!!data?.truncateComments}
              onChange={(truncateComments) => setFlag({ truncateComments })}
            />
            <FlagToggle
              title="Gäste-Kommentar bearbeitbar"
              description="Aktiviert das Bearbeiten des persönlichen Gäste-Kommentars direkt aus der Reservierungs-Sidebar."
              checked={!!data?.editGuestComment}
              onChange={(editGuestComment) => setFlag({ editGuestComment })}
            />
            <FlagToggle
              title="„+15 Min“-Puffer ausblenden"
              description="Versteckt die Schaltfläche, mit der einer Reservierung 15 Minuten Pufferzeit hinzugefügt werden können."
              checked={!!data?.hidePlusFifteen}
              onChange={(hidePlusFifteen) => setFlag({ hidePlusFifteen })}
            />
            <FlagToggle
              title="Reservierungs-Code in der Karte anzeigen"
              description="Stellt der Reservierungskarte den eigenen Code (#-Nummer) der Reservierung voran."
              checked={!!data?.showCode}
              onChange={(showCode) => setFlag({ showCode })}
            />
            <FlagToggle
              title="„Bezahlt“-Button statt Status-Auswahl"
              description="In der Reservierungs-Sidebar erscheint ein „Bezahlt“-Button anstelle des Status-Dropdowns."
              checked={!!data?.paidButton}
              onChange={(paidButton) => setFlag({ paidButton })}
            />
            <FlagToggle
              title="Nur Reservierungen der aktuellen Schicht anzeigen"
              description="Heute öffnet das Reservierungsbuch in der laufenden Schicht statt in „Ganzer Tag“ und wechselt zum Beginn der nächsten Schicht mit. Eine von Hand gewählte Schicht bleibt stehen. Eine Reservierung genau zum Schichtwechsel zählt zur beginnenden Schicht."
              checked={!!data?.onlyShowReservationsThatStartInCurrentShift}
              onChange={(onlyShowReservationsThatStartInCurrentShift) =>
                setFlag({ onlyShowReservationsThatStartInCurrentShift })
              }
            />
            <FlagToggle
              title="Hinweis bei Tischwechsel ausblenden"
              description="Beim Verschieben einer Reservierung auf einen anderen Tisch erscheint kein Bestätigungs-Hinweis mehr."
              checked={!!data?.disableRelocateMessage}
              onChange={(disableRelocateMessage) =>
                setFlag({ disableRelocateMessage })
              }
            />
            <FlagToggle
              title="Eigenes Feld „Verspätung“"
              description="Aktiviert ein zusätzliches Feld, mit dem die Verspätung eines Gastes erfasst werden kann."
              checked={!!data?.customFieldBeingLate}
              onChange={(customFieldBeingLate) =>
                setFlag({ customFieldBeingLate })
              }
            />
            <FlagToggle
              title="Platzierte Gäste ausblenden"
              description="Reservierungen, deren Gäste bereits am Tisch sitzen, werden in der linken Sidebar nicht mehr angezeigt."
              checked={!!data?.hidePlacedGuests}
              onChange={(hidePlacedGuests) => setFlag({ hidePlacedGuests })}
            />
            <FlagToggle
              title="Tisch automatisch nach Ablauf der Reservierungslänge auf fertig setzen"
              description="Eine bereits begonnene Reservierung wird automatisch als fertig markiert, sobald die Reservierungslänge abgelaufen ist."
              checked={!!data?.autoSetTableToDoneAfterEnd}
              onChange={(autoSetTableToDoneAfterEnd) =>
                setFlag({ autoSetTableToDoneAfterEnd })
              }
            />
            <FlagToggle
              title="Bestätigungsmail standardmäßig deaktiviert"
              description="Beim Anlegen, In-House-Erstellen und Bearbeiten von Reservierungen ist die Option „Bestätigungsmail senden“ standardmäßig ausgeschaltet. Das Personal kann sie pro Reservierung weiterhin manuell aktivieren."
              checked={!!data?.confirmationEmailOffByDefault}
              onChange={(confirmationEmailOffByDefault) =>
                setFlag({ confirmationEmailOffByDefault })
              }
            />
          </Section>

          {/* ── Walk-Ins ───────────────────────────────────────────────────── */}
          <Section title="Walk-Ins">
            <FlagToggle
              title="Maximale Gästezahl voreinstellen"
              description="Beim Anlegen eines Walk-Ins wird die maximale Tischkapazität als Gästezahl voreingetragen (statt der minimalen)."
              checked={!!data?.useMaxGuestsForWalkin}
              onChange={(useMaxGuestsForWalkin) =>
                setFlag({ useMaxGuestsForWalkin })
              }
            />
            <FlagInput
              title="Standard-Anlass für Walk-Ins"
              description="ID des Anlasses, der beim Anlegen eines Walk-Ins automatisch ausgewählt wird."
              value={data?.walkinDefaultOccassion ?? ''}
              onChange={(walkinDefaultOccassion) =>
                setFlag({ walkinDefaultOccassion })
              }
            />
          </Section>

          {/* ── Layout & Eingabe ───────────────────────────────────────────── */}
          <Section title="Layout & Eingabe">
            <FlagInput
              title="Standard-Ländercode für Telefonnummern"
              description="Wird im Gäste-Auswahlfeld vorausgewählt. Beispiele: de, at, ch."
              value={data?.phoneDefaultCountry ?? 'de'}
              onChange={(phoneDefaultCountry) =>
                setFlag({ phoneDefaultCountry })
              }
            />
            <FlagInput
              title="Breite der Sidebar"
              description="Überschreibt die Standardbreite der linken Sidebar (in Pixeln). Leer lassen für Standard."
              value={data?.sidebarWidth ?? ''}
              type="number"
              onChange={(sidebarWidth) => setFlag({ sidebarWidth })}
            />
            <FlagInput
              title="Schriftgröße in der Sidebar"
              description="Schriftgröße in Pixeln innerhalb der linken Sidebar. Leer lassen für Standard."
              value={data?.sidebarFontSize ?? ''}
              type="number"
              onChange={(sidebarFontSize) => setFlag({ sidebarFontSize })}
            />
            <FlagInput
              title="Deaktivierte Zusatzfelder"
              description="Komma-getrennte Liste von Zusatzfeld-IDs, die im UI nicht angezeigt werden sollen."
              value={data?.disabledCustomFields ?? ''}
              onChange={(disabledCustomFields) =>
                setFlag({ disabledCustomFields })
              }
            />
          </Section>

          {/* ── System ─────────────────────────────────────────────────────── */}
          <Section title="System">
            <FlagToggle
              title="Bestätigungs-Toasts ausblenden"
              description="Versteckt die Pop-up-Meldungen am unteren Bildschirmrand, mit denen Aktionen rückgängig gemacht werden können."
              checked={!!data?.hideToasts}
              onChange={(hideToasts) => setFlag({ hideToasts })}
            />
            <FlagToggle
              title="Erweiterte Sicherheit für Admins deaktivieren"
              description="Admins müssen sich bei sensiblen Aktionen nicht erneut authentifizieren (2-Faktor / Code-Eingabe)."
              checked={!!data?.disableSecurityForAdmins}
              onChange={(disableSecurityForAdmins) =>
                setFlag({ disableSecurityForAdmins })
              }
            />
          </Section>

          {/* ── Tisch-Status ───────────────────────────────────────────────── */}
          <Section title="Tisch-Status">
            <FlagToggle
              title="Mehrere Status pro Tisch erlauben"
              description="Erlaubt das gleichzeitige Setzen mehrerer benutzerdefinierter Status auf demselben Tisch."
              checked={!!data?.allowMultipleStati}
              onChange={(allowMultipleStati) => setFlag({ allowMultipleStati })}
            />

            <Typography
              variant="text-3"
              color="subdued"
              translation={null}
              block
              className="exp-flags__description"
              style={{ marginBottom: 8 }}
            >
              Eigene Status-Einträge mit Emoji, Bezeichnung und Farben für Tisch
              und Stuhl. Reihenfolge per Pfeil-Buttons anpassbar.
            </Typography>

            {data?.currentTableStati?.map((t, i, arr) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                >
                  <IconButton
                    size="small"
                    disabled={!i}
                    onClick={() => {
                      ref.set(
                        {
                          currentTableStati: changePositionInArray(
                            arr,
                            i,
                            i - 1,
                          ),
                        },
                        { merge: true },
                      );
                    }}
                  >
                    <ArrowUpward />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={arr.length - 1 === i}
                    onClick={() => {
                      ref.set(
                        {
                          currentTableStati: changePositionInArray(
                            arr,
                            i,
                            i + 1,
                          ),
                        },
                        { merge: true },
                      );
                    }}
                  >
                    <ArrowDownward />
                  </IconButton>
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: 10,
                    }}
                  >
                    <TextField
                      label="Emoji"
                      value={t.icon}
                      onChange={(e) => {
                        ref.set(
                          {
                            currentTableStati: arr.map((x, i1) =>
                              i1 === i ? { ...x, icon: e.target.value } : x,
                            ),
                          },
                          { merge: true },
                        );
                      }}
                      style={{
                        width: 60,
                      }}
                    />
                    <TextField
                      label="Bezeichnung"
                      value={t.title}
                      style={{ flex: 1 }}
                      fullWidth
                      onChange={(e) => {
                        ref.set(
                          {
                            currentTableStati: arr.map((x, i1) =>
                              i1 === i ? { ...x, title: e.target.value } : x,
                            ),
                          },
                          { merge: true },
                        );
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: 10,
                    }}
                  >
                    <TextField
                      label="Tisch-Farbe (auch Farbe der Reservierung)"
                      value={t.color}
                      fullWidth
                      helperText="Beispiel: #ffffff oder rgb(255,255,255)"
                      onChange={(e) => {
                        ref.set(
                          {
                            currentTableStati: arr.map((x, i1) =>
                              i1 === i ? { ...x, color: e.target.value } : x,
                            ),
                          },
                          { merge: true },
                        );
                      }}
                    />
                    <TextField
                      label="Stuhl-Farbe"
                      value={t.chairColor || t.color}
                      fullWidth
                      helperText="Beispiel: #ffffff oder rgb(255,255,255)"
                      onChange={(e) => {
                        ref.set(
                          {
                            currentTableStati: arr.map((x, i1) =>
                              i1 === i
                                ? { ...x, chairColor: e.target.value }
                                : x,
                            ),
                          },
                          { merge: true },
                        );
                      }}
                    />
                  </div>
                </div>
                <div style={{ flexDirection: 'column', gap: 10 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      ref.set(
                        {
                          currentTableStati: arr.filter((x, i1) => i1 !== i),
                        },
                        { merge: true },
                      );
                    }}
                  >
                    <Delete />
                  </IconButton>
                </div>
              </div>
            ))}
            <Button
              onClick={() => {
                ref.set(
                  {
                    currentTableStati: FieldValue.arrayUnion({
                      title: '',
                      color: '',
                      chairColor: '',
                      icon: '',
                      textColor: '#ffffff',
                    }),
                  },
                  { merge: true },
                );
              }}
            >
              Tisch-Status hinzufügen
            </Button>
          </Section>
        </>
      )}
    </Modal>
  );
};

export default ExperimentalFlags;
